import React    from 'react';
import ReactDOMClient from 'react-dom/client';

import DocEvents           from 'UTILS/events';
import STPApp         from './stp.app';

import { capitalize } from 'HELPERS/text';
import { isNumber } from 'HELPERS/validator';

import normalizeOptions from 'UTILS/Options';
import Context from 'UTILS/Context';
import Mixpanel from 'UTILS/Vendor/Mixpanel';
import Session from 'UTILS/Session';
import LokaliseLanguage from 'UTILS/LokaliseLanguage';
import Emitter from 'UTILS/Emitter';

import PreferabliAnalytics from 'UTILS/Vendor/analytics';
import IntegrationsApi from 'API/IntegrationsApi';

"use strict";

class PreferabliSTP {
	constructor(element, options) {
		const defaults = {
			gallery: {
				type: 'carousel',
				productsToDisplay: 4,
			},
			display: {
				separator: null,
				layout: 'row',
				use_preferabli_logo: false,
				logo_placement: 'top-right',
				logo_option: 9,
				logo_inline_style: {},
				logo_class: '',
			},
			hideHeading: false,
			helpTooltip: false,
			devMode: false,
			openNewTab: false,
			showDecimalPlaces: true,
			numDecimalPlaces: 2,
			showLink: true,
			customText: null,
			renderProductCards: null,
			onRenderComplete: null,
			productCardsContainer: null,
			lang: 'en',
			opt_out_tracking: false,
		};

		this.__version = __VERSION__;

		if(!options.element && element){
			options.element = document.querySelector(element);
		}

		this.opts = normalizeOptions({ ...defaults, ...options });

		this._lang = new LokaliseLanguage();

		this._api = new IntegrationsApi();
		this._api.setup({
			integration_id: this.opts.integration_id,
			opts: this.opts,
			lang: this._lang,
		});


		this._analytics = new PreferabliAnalytics();
		this._analytics.setup({
			integration_id: this.opts.integration_id,
			opt_out_tracking: this.opts.opt_out_tracking
		});

		this._context = new Context();
		this._context.setOptions(this.opts);
		this._context.setIntegrationId(this.opts.integration_id);
		this._context.setLanguage(this.opts.lang);

		this.render({
			...this.opts,
		});
		
	}

	handleError = (error = '') => {
		document.dispatchEvent(new CustomEvent(DocEvents.loadError, {detail: error}));
		document.dispatchEvent(new CustomEvent('preferabli.lttt.onError', {detail: error}));
		document.dispatchEvent(new CustomEvent('preferabli.stp.onError', {detail: error}));
	}

	getLTTT = (product_params) => {
		this.getProducts(product_params);
	}

 	getProducts = async (params = {}) => {
		const { collection_id } = this.opts;

		const param_keys = Object.keys(params);

		if (param_keys.length === 0) {
			this.handleError(error);
		}

		const check_params_values = Object.keys(params).every(
			(param) => typeof params[param] !== 'undefined' && params[param] !== null
		);

		if (!check_params_values) {
			throw "Either 'product_id' was invalid format. Please make sure that 'product_id' is a string or numeric format.";
			this.handleError(error);
		}

		if (check_params_values) {

			this._context.setProductId(params.product_id);
			this._analytics.setProductId(params.product_id);

			const products = await this.fetchLTTT({
				collection_id,
				...params,
			}).catch((error) => {

				this._analytics.track('lttt results not loaded', {
					error: 'api',
				    ...params,
				});

				return (null);
			});

			if (products) {

				const details = Array.isArray(products.merchant_vintage_details)
				  ? products.merchant_vintage_details
				  : [];


				const updateProducts = new CustomEvent(DocEvents.updateProducts, {
					detail: {
						products,
						product_id: params.product_id,
					},
				});

				document.dispatchEvent(updateProducts);
				
				const hasResults = Array.isArray(details) && details.length > 0;
				const detail = { has_results: hasResults };

				document.dispatchEvent(new CustomEvent('preferabli.lttt.onComplete', { detail }));
				document.dispatchEvent(new CustomEvent('preferabli.stp.onComplete', { detail }));

				this._analytics.track('lttt results returned', {
				  number_of_products: details.length,
				  product_ids: details.map(d => d.product_id),
				  product_names: details.map(d => d.product_name),
				  lookup_ids: details.map(d => d.id),
				  ...params,
				});
			} 

			if(!products){
				this.handleError();
			}
		}
	}

	fetchLTTT = (options) => {
		const {
			collection_id, landing_url, product_id, price_max_percent, price_min_percent, limit
		} = options;

		let params = {
			collection_id,
		}

		if(product_id && String(product_id).length){
			params.merchant_variant_id = String(product_id);
		}

		if(landing_url && String(landing_url).length){
			params.landing_url = encodeURIComponent(String(landing_url));
		}

		if (price_max_percent && Number(price_max_percent) > 0) {
			params.price_max_percent = Number(price_max_percent);
		}
		if (price_min_percent && Number(price_min_percent) > 0) {
			params.price_min_percent = Number(price_min_percent);
		}

		if(limit && isNumber(String(limit))) params.limit = Number(limit);


		return this._api.getProductLttt({
			...params,
		});
	}

	createAttchElement = (attachElem, newElement) => {
		var elementDiv = document.createElement('div');
		elementDiv.id = newElement.replace('#', '');

		if (typeof attachElem === 'string' || !(attachElem instanceof Element)) {
			if (document.querySelector(attachElem).nodeName === 'BODY') {
				const bodyEl = document.querySelector(attachElem);
				const firstScript = Object.values(document.querySelector('body').childNodes).find(
					(htmlTag) => htmlTag.nodeName === 'SCRIPT'
				);

				document.querySelector(attachElem).insertBefore(elementDiv, firstScript);
			}
			if (document.querySelector(attachElem)) {
				document.querySelector(attachElem).appendChild(elementDiv);
			}
		}
		if (attachElem instanceof Element) {
			attachElem.appendChild(elementDiv);
			resolve();
		}
		return elementDiv;
	}

	render = async (params) => {

		let _root;

		await this._lang.load(this.opts.lang);  // load before rendering app

		const { element, ...renderParams } = params;

		const rootNode = (element && element instanceof Element) ? element : !document.querySelector('#lttt_placeholder')
				? this.createAttchElement('body', 'lttt_placeholder')
				: document.querySelector('#lttt_placeholder');


		_root = ReactDOMClient.createRoot(rootNode);

		const _props = {
			...renderParams, 
			context: this._context, 
			api: this._api, 
			analytics: this._analytics,
			lang: this._lang,
		};

		_root.render(<STPApp { ..._props } />);
	}
}

const fetchElement = (element) => document.getElementById(element) || document.querySelector(element);

window.PreferabliSTP = PreferabliSTP;
window.PreferabliLTTT = PreferabliSTP;
window.WRLikeThatTryThis = PreferabliSTP;

export default PreferabliSTP;
